import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MenuProps } from 'antd/lib/menu';
import { Button, Dropdown, Menu, message, Popconfirm, Space, Spin, Tabs, Typography } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import ActivityHistoryTab from './ActivityHistoryTab';
import AZInsightTab from './AZInsightTab';
import PlansPluginsTab from './PlansPluginsTab';
import ProfileTab from './ProfileTab';
import SPApiTab from './SPApiTab';
import SubAccountTab from './SubAccountTab';
import { extendTrialAZ, logoutUserFromServices, removeAccount, syncMarketplaceConnectionsFromPortal, updateUserInfo } from 'services';
import { Helmet } from 'react-helmet';
import StripeTab from './StripeTab';
import { getUserInfo } from '../../../services/user';

const listOfTabs = [
  {
    label: 'Profile',
    key: 'profileTab',
  },
  {
    label: 'Plans & Plugins',
    key: 'plansPluginsTab',
  },
  {
    label: 'Integration',
    key: 'spapiTab',
  },
  {
    label: 'Sub accounts',
    key: 'subaccountTab',
  },
  {
    label: 'AZInsight',
    key: 'azinsightTab',
  },
  {
    label: 'Stripe',
    key: 'stripeTab',
  },
  {
    label: 'Activity History',
    key: 'activityHistoryTab',
  },
]

const ComponentTab = ({ tab, props, onReFetchUser }: any) => {
  switch (tab) {
    case 'plansPluginsTab':
      return <PlansPluginsTab data={props} onReFetchUser={onReFetchUser} />;
    case 'spapiTab':
      return <SPApiTab data={props} />;
    case 'subaccountTab':
      return <SubAccountTab data={props} />;
    case 'azinsightTab':
      return <AZInsightTab data={props} />;
    case 'stripeTab':
      return <StripeTab data={props} />;
    case 'activityHistoryTab':
      return <ActivityHistoryTab data={props} />;
    default:
      return <ProfileTab data={props} onReFetchUser={onReFetchUser} />;
  }
}

const UserOverview = () => {
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingLogoutButton, setLoadingLogoutButton] = useState<boolean>(false);
  const [loadingExtendButton, setLoadingExtendButton] = useState<boolean>(false);
  const [loadingSyncButton, setLoadingSyncButton] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>();
  const [activedTab, setActivedTab] = useState<string>('profileTab');
  const [loadingField, setLoadingField] = useState<boolean>(false);
  const [mauticContactLink, setMauticContactLink] = useState<string>('');
  const [reFetchUser, setReFetchUser] = useState<boolean>(false);

  let { email: userEmail } = useParams();
  let navigate = useNavigate();
  let location = useLocation();

  const loadProfile = (email: string) => {
    getUserInfo(email)
      .then(({ data: res }: any) => {
        setLoadingData(false);
        setUserData(() => {
          setMauticContactLink(res.user.mauticContactId);
          return res
        });
        if (location.hash) {
          setActivedTab(location.hash.slice(1));
        }
      })
      .catch((err) => {
        setLoadingData(false);
        message.error("User not found");
        navigate(`../user-management`);
      })
  }

  //Handle
  const handleOnChangeTab = (key: string) => {
    setActivedTab(key);
    navigate(`#${key}`, { replace: true })
  }

  const handleOnReFetchUser = (refetch: boolean) => setReFetchUser(refetch);

  const handleMenuLogoutClick: MenuProps['onClick'] = e => {
    setLoadingLogoutButton(true);
    const params = {
      id: userData?.user._id,
      type: e.key,
    }
    logoutUserFromServices(params)
      .then(() => {
        message.success('Logged out!!');
        setLoadingLogoutButton(false);
      })
  };

  const handleMenuExtendClick: MenuProps['onClick'] = e => {
    setLoadingExtendButton(true);
    const params = {
      id: userData?.user._id,
      days: parseInt(String(e.key), 10),
    }
    extendTrialAZ(params)
      .then(() => {
        message.success('Extended AZInsight trial successfully');
        setLoadingExtendButton(false);
      })
      .catch(() => {
        message.error('Extend AZInsight trial failed');
        setLoadingExtendButton(false);
      })
  };

  const handleSyncMarketplaceConnections = () => {
    const userId = userData?.user?._id;
    if (!userId) {
      return;
    }
    setLoadingSyncButton(true);
    syncMarketplaceConnectionsFromPortal(userId)
      .then(({ data: res }: any) => {
        const upserted = res?.data?.upserted || [];
        const errors = res?.data?.errors || [];
        if (errors.length) {
          message.warning(`${res?.message || 'Synced'}: ${errors.join('; ')}`);
        } else {
          message.success(`Synced ${upserted.join(', ') || 'nothing new'} into marketplace connections`);
        }
      })
      .catch((err: any) => message.error(err?.response?.data?.message || 'Sync failed'))
      .finally(() => setLoadingSyncButton(false));
  };

  const handleOnChangeEmailInline = (text: string) => {
    if (text === userEmail) {
      return;
    }

    setLoadingField(true);

    const params = {
      value: text,
      field: 'email',
      id: userData.user._id
    };
    updateUserInfo(params)
      .then(() => {
        setLoadingField(false);
        navigate(`../user/${text}`);
      })
      .catch(() => {
        message.error('Failed in update email');
        setLoadingField(false);
      })
  };

  const handleOnDeleteUser = (id: string) => {
    if (!userData?.user?.mauticContactId) {
      message.error("Cannot get user contact ID");
      return;
    }
    
    setLoadingData(true);
    removeAccount(String(userData.user.mauticContactId))
      .then(() => {
        setLoadingData(false);
        message.success('Deleted user successfully!!');
        navigate(`../user-management`);
      })
      .catch(() => {
        message.error("Couldn't delete user");
        setLoadingData(false);
      })
  };

  const menuLogout = (
    <Menu
      onClick={handleMenuLogoutClick}
      items={[
        {
          label: 'Logout ADDON',
          key: 'ADDON',
        },
        {
          label: 'Logout AZINSIGHT',
          key: 'AZINSIGHT',
        },
        {
          label: 'Logout AZMOBILE',
          key: 'AZMOBILE',
        },
        {
          label: 'Logout ALL',
          key: 'ALL',
        },
      ]}
    />
  );

  const menuExtend = (
    <Menu
      onClick={handleMenuExtendClick}
      items={[
        {
          label: '10 days',
          key: '10',
        },
        {
          label: '20 days',
          key: '20',
        },
        {
          label: '30 days',
          key: '30',
        },
      ]}
    />
  );

  useEffect(() => {
    if (userEmail) {
      setLoadingData(true);
      loadProfile(userEmail);
    }
  }, [userEmail])

  useEffect(() => {
    if (reFetchUser) {
      if (userEmail) {
        loadProfile(userEmail);
      }
      setReFetchUser(false);
    }
  }, [reFetchUser])

  return (
    <>
      <Helmet>
        <title>{userEmail} | User management</title>
      </Helmet>
      <PageHeader
        className="page-header"
        title={
          <Space>User Email:
            {loadingField ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            ) : (
              <Typography.Title
                level={4}
                style={{ margin: 0 }}
                italic
                copyable
                editable={mauticContactLink ? {
                  onChange: (text: string) => handleOnChangeEmailInline(text)
                } : false}
              >
                {userEmail}
              </Typography.Title>
            )}
          </Space>
        }
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {listOfTabs.map(i => (
              <Tabs.TabPane tab={i.label} key={i.key}>
                {loadingData ? (
                  <div className="loading-page"><Spin size="large" /></div>
                ) : (
                  userData && <ComponentTab tab={activedTab} props={userData} onReFetchUser={handleOnReFetchUser} />
                )}
              </Tabs.TabPane>
            ))}
          </Tabs>
        }
        style={{
          height: '100%',
          paddingBottom: 30
        }}
      >
        <Space key="versionn-select" direction="vertical" style={{ marginRight: 30, width: '100%' }}>
          <Space wrap style={{ width: '100%', justifyContent: 'end' }}>
            <Button type="primary" disabled={loadingData || !mauticContactLink}><a href={`https://mautic.asinzen.com/s/contacts/view/${mauticContactLink}`} target="_blank" rel="noreferrer">Open contact in Mautic</a></Button>
            <Dropdown overlay={menuLogout} disabled={loadingData || loadingLogoutButton}>
              <Button>
                <Space>
                  Logout
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
            <Dropdown overlay={menuExtend} disabled={loadingData || loadingExtendButton}>
              <Button>
                <Space>
                  Extend AZInsight Trial
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
            <Button
              loading={loadingSyncButton}
              disabled={loadingData || !userData?.user?._id}
              onClick={handleSyncMarketplaceConnections}
            >
              Sync to CommandHub
            </Button>
            <Popconfirm
              placement="bottomLeft"
              title="Are you sure to delete this user?"
              onConfirm={() => handleOnDeleteUser(userData.user._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger disabled={loadingData}>Delete from DB & Mautic & Stripe</Button>
            </Popconfirm>
          </Space>
        </Space>
      </PageHeader>
    </>
  )
}

export default UserOverview;